import { useState, useEffect } from 'react';
import { Lock, Key } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const { updatePassword, user } = useAuth();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery' || user) {
      setHasRecoverySession(true);
    } else {
      setMessage('Invalid or expired reset link. Please request a new password reset.');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await updatePassword(password);
      setMessage('Password updated successfully! Redirecting to login...');
      setIsSuccess(true);

      setTimeout(() => {
        window.location.href = '/crm';
      }, 2000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to update password');
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex items-center justify-center p-6">
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 w-full max-w-md border border-white/20">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-amber-500 p-4 rounded-full">
            <Lock className="w-8 h-8 text-white" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-white text-center mb-2">
          Set New Password
        </h1>
        <p className="text-white/70 text-center mb-6">
          Enter your new password below
        </p>

        {message && (
          <div className={`mb-4 p-3 rounded-lg ${isSuccess ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>
            {message}
          </div>
        )}

        {!hasRecoverySession && !message && (
          <div className="mb-4 p-3 rounded-lg bg-yellow-500/20 text-yellow-200">
            Verifying reset link...
          </div>
        )}

        {hasRecoverySession && <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/80 mb-2 text-sm font-semibold">New Password</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
                className="w-full bg-white/10 text-white rounded-lg pl-11 pr-4 py-3 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-white/40"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/80 mb-2 text-sm font-semibold">Confirm Password</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={6}
                className="w-full bg-white/10 text-white rounded-lg pl-11 pr-4 py-3 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-white/40"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || isSuccess}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-500 text-white font-bold py-3 rounded-lg transition-all transform hover:scale-105 disabled:scale-100"
          >
            {loading ? 'Updating...' : isSuccess ? 'Success!' : 'Update Password'}
          </button>
        </form>}
      </div>
    </div>
  );
}
